import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyPortal } from '@/components/BodyPortal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Snackbar } from '@/components/Snackbar';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Layout } from '@/layouts/Layout';
import { MailboxView } from '@/pages/Mail/components/MailboxView';
import { MailSearchOverlay } from '@/pages/Mail/components/MailSearchOverlay';
import { MailSegmentedControl } from '@/pages/Mail/components/MailSegmentedControl';
import { MailAttachmentPreview } from '@/pages/Mail/components/MailAttachmentPreview';
import { SurveyDetailModal } from '@/pages/Mail/components/SurveyDetailModal';
import { SurveyList } from '@/pages/Mail/components/SurveyList';
import { VideoDetailModal } from '@/pages/Mail/components/VideoDetailModal';
import { VideoList } from '@/pages/Mail/components/VideoList';
import { useMailFolders } from '@/pages/Mail/hooks/useMailFolders';
import { useMailMessages } from '@/pages/Mail/hooks/useMailMessages';
import { useMailSearch } from '@/pages/Mail/hooks/useMailSearch';
import { useMarkSurveySeen } from '@/pages/Mail/hooks/useMarkSurveySeen';
import { useStartSurvey } from '@/pages/Mail/hooks/useStartSurvey';
import { useSurveys } from '@/pages/Mail/hooks/useSurveys';
import { useJoinVideoMeeting } from '@/pages/Mail/hooks/useJoinVideoMeeting';
import { useRespondToVideoMeeting } from '@/pages/Mail/hooks/useRespondToVideoMeeting';
import { useVideoMeetings } from '@/pages/Mail/hooks/useVideoMeetings';
import {
  MailFolderType,
  MailSection,
  SnackbarVariant,
  SurveyDetailState,
} from '@/types/components';
import type { MailAttachment } from '@/types/mail';

export const Mail: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [section, setSection] = useState(MailSection.Mail);
  const [folderType, setFolderType] = useState(MailFolderType.Inbox);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string | null>(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(
    null,
  );
  const [decliningMeetingId, setDecliningMeetingId] = useState<string | null>(
    null,
  );
  const [previewAttachment, setPreviewAttachment] =
    useState<MailAttachment | null>(null);
  const [isSurveySubmittedSnackbarOpen, setIsSurveySubmittedSnackbarOpen] =
    useState(false);
  const [isFillRequested, setIsFillRequested] = useState(false);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);

  const isMailSection = section === MailSection.Mail;
  const isSurveysSection = section === MailSection.Surveys;
  const isVideoSection = section === MailSection.VideoCalls;

  const { data: folders = [], isLoading: isFoldersLoading } =
    useMailFolders(isMailSection);

  const { data: surveys = [], isLoading: isSurveysLoading } =
    useSurveys(isSurveysSection);

  const { data: meetings = [], isLoading: isMeetingsLoading } =
    useVideoMeetings(isVideoSection);

  const selectedMeeting =
    meetings.find(({ id }) => id === selectedMeetingId) ?? null;

  const { respondingId, approve, decline } = useRespondToVideoMeeting(() =>
    setIsErrorSnackbarOpen(true),
  );
  const joinMeeting = useJoinVideoMeeting({
    meetings,
    onError: () => setIsErrorSnackbarOpen(true),
  });

  const declineMeeting = () => {
    if (!decliningMeetingId) {
      return;
    }

    decline(decliningMeetingId);
    setDecliningMeetingId(null);
  };

  const selectedSurvey =
    surveys.find(({ id }) => id === selectedSurveyId) ?? null;

  const { markSeen } = useMarkSurveySeen();
  const { startSurvey } = useStartSurvey();

  const selectSurvey = (id: string) => {
    const isUnseen = surveys.find((survey) => survey.id === id)?.isNew;

    setSelectedSurveyId(id);

    if (isUnseen) {
      markSeen(id);
    }
  };

  const openSurvey = (id: string) => {
    selectSurvey(id);
    setIsFillRequested(false);
  };

  const fillSurvey = (id: string) => {
    const isSurveyNew =
      surveys.find((survey) => survey.id === id)?.detailState ===
      SurveyDetailState.New;

    selectSurvey(id);
    setIsFillRequested(true);

    if (isSurveyNew) {
      startSurvey(id);
    }
  };

  const selectedFolder =
    folders.find(({ type }) => type === folderType) ?? folders[0] ?? null;

  const openedFolderType = isMailSection
    ? (selectedFolder?.type ?? folderType)
    : null;

  const search = useMailSearch(selectedFolder);
  const messages = useMailMessages({
    folderType: openedFolderType,
    searchType: search.searchType,
    searchValue: search.submittedQuery,
    onFollowUpError: () => setIsErrorSnackbarOpen(true),
  });
  const { selectedMailId } = messages;

  const openMailFromSearch = (id: string) => {
    search.hideSearch();
    messages.openMail(id);
  };

  const submitSurvey = () => {
    setSelectedSurveyId(null);
    setIsSurveySubmittedSnackbarOpen(true);
  };

  const changeFolder = (nextFolderType: MailFolderType) => {
    messages.closeMail();
    setFolderType(nextFolderType);
  };

  const changeSection = (nextSection: MailSection) => {
    messages.closeMail();
    search.resetSearch();
    setSelectedSurveyId(null);
    setSelectedMeetingId(null);
    setFolderType(MailFolderType.Inbox);
    setSection(nextSection);
  };

  return (
    <>
      <Layout
        desktopHeader={
          <MailSegmentedControl value={section} onChange={changeSection} />
        }
        hideMobileNavbar={!!selectedMailId}
      >
        {!(isMailSection && selectedMailId) && (
          <div className="px-4 pt-2 pb-4 md:hidden">
            <MailSegmentedControl value={section} onChange={changeSection} />
          </div>
        )}

        <div key={section} className="flex min-h-0 flex-1 flex-col">
          {isMailSection && isFoldersLoading && (
            <div className="flex flex-1 items-center justify-center">
              <PageSpinner className="size-[22px] text-subtle-foreground" />
            </div>
          )}

          {isMailSection && selectedFolder && (
            <MailboxView
              isMobile={isMobile}
              folders={folders}
              folder={selectedFolder}
              messages={messages}
              search={search}
              onFolderChange={changeFolder}
            />
          )}

          {isSurveysSection && isSurveysLoading && (
            <div className="flex flex-1 items-center justify-center">
              <PageSpinner className="size-[22px] text-subtle-foreground" />
            </div>
          )}

          {isSurveysSection && !isSurveysLoading && (
            <div className="flex min-h-0 flex-1 flex-col max-md:animate-zoom-in md:p-6">
              <SurveyList
                surveys={surveys}
                isMobile={isMobile}
                onOpen={openSurvey}
                onFill={fillSurvey}
              />
            </div>
          )}

          {isVideoSection && isMeetingsLoading && (
            <div className="flex flex-1 items-center justify-center">
              <PageSpinner className="size-[22px] text-subtle-foreground" />
            </div>
          )}

          {isVideoSection && !isMeetingsLoading && (
            <div className="flex min-h-0 flex-1 flex-col max-md:animate-zoom-in md:p-6">
              <VideoList
                meetings={meetings}
                isMobile={isMobile}
                respondingId={respondingId}
                onOpen={setSelectedMeetingId}
                onJoin={joinMeeting}
                onApprove={approve}
                onDecline={setDecliningMeetingId}
              />
            </div>
          )}
        </div>

        {selectedSurvey && (
          <SurveyDetailModal
            survey={selectedSurvey}
            isMobile={isMobile}
            isFillRequested={isFillRequested}
            onClose={() => setSelectedSurveyId(null)}
            onSubmitted={submitSurvey}
            onError={() => setIsErrorSnackbarOpen(true)}
          />
        )}

        {selectedMeeting && (
          <VideoDetailModal
            meeting={selectedMeeting}
            isMobile={isMobile}
            isResponding={selectedMeeting.id === respondingId}
            onClose={() => setSelectedMeetingId(null)}
            onJoin={joinMeeting}
            onApprove={approve}
            onDecline={setDecliningMeetingId}
            onOpenAttachment={setPreviewAttachment}
          />
        )}

        <ConfirmDialog
          isOpen={!!decliningMeetingId}
          title={t('mail.video.detail.declineConfirm.title')}
          message={t('mail.video.detail.declineConfirm.message')}
          confirmLabel={t('mail.video.detail.declineConfirm.confirm')}
          onClose={() => setDecliningMeetingId(null)}
          onConfirm={declineMeeting}
        />

        {previewAttachment && (
          <MailAttachmentPreview
            attachment={previewAttachment}
            onClose={() => setPreviewAttachment(null)}
          />
        )}

        <Snackbar
          isOpen={isSurveySubmittedSnackbarOpen}
          message={t('mail.surveys.detail.fill.submitSuccess')}
          onDismiss={() => setIsSurveySubmittedSnackbarOpen(false)}
        />

        <BodyPortal>
          <Snackbar
            isOpen={isErrorSnackbarOpen}
            variant={SnackbarVariant.Error}
            message={t('global.errorOccurred')}
            onDismiss={() => setIsErrorSnackbarOpen(false)}
          />
        </BodyPortal>
      </Layout>

      {selectedFolder && (
        <MailSearchOverlay
          folder={selectedFolder}
          search={search}
          messages={messages}
          onOpenMail={openMailFromSearch}
        />
      )}
    </>
  );
};
